<template>
  <LAutocomplete
    :id="fieldId"
    :label="alias"
    :disabled="isDisabled"
    :errors="error"
    :infos="infoMessage"
    :options="options"
    :model-value="stringModelValue"
    @update:model-value="handleInput"
  >
    <template v-if="appendIcon" #append-icon>
      <LIcon icon="lnx-info" @click="openModal" />
    </template>
  </LAutocomplete>
</template>

<script lang="ts" setup>
import { LAutocomplete, LIcon } from '@wgg.hyve/hyve-ui-library'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type Item = string | number | { title: string; value: string | number }

type Props = {
  fieldId: string
  disabled: boolean
  items: Item[]
  modelValue: string | number | null | undefined
  error: string
  alias: string
  prependIcon: string
  appendIcon: string
  modalContent: string
  infoMessage: string
  noDataText?: string
  readOnly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  items: () => [],
  modelValue: '',
  error: '',
  alias: '',
  prependIcon: '',
  appendIcon: '',
  modalContent: '',
  infoMessage: '',
  noDataText: 'No data found'
})
const { t } = useI18n()

const emit = defineEmits<{
  (e: 'openModal', content: string): void
  (e: 'update:modelValue', value: string | number | undefined): void
}>()

type Option = { label: string; value: string }

const isDisabled = computed(() => !!props.disabled || !!props.readOnly)
const stringModelValue = computed(() =>
  props.modelValue === null || props.modelValue === undefined ? undefined : String(props.modelValue)
)

const options = computed<Option[]>(() =>
  props.items.map((option) =>
    typeof option === 'object' ? { label: t(option.title), value: String(option.value) } : { label: String(option), value: String(option) }
  )
)

const handleInput = (value: unknown) => {
  let normalized: string | number | undefined
  if (Array.isArray(value)) {
    normalized = value[0]
  } else {
    normalized = value as string | number | undefined
  }
  if (normalized === undefined || normalized === null) return

  emit('update:modelValue', normalized)
}

const openModal = () => {
  if (props.modalContent) {
    emit('openModal', props.modalContent)
  }
}
</script>
